"use client";

/**
 * WrappedStoriesPlayer — shared Wrapped overlay engine.
 *
 * Extracted from `niche-v3/WrappedFinale.tsx` (S84) to remove the duplicated
 * `plan/WrappedPNGPlayer.tsx` implementation. Niche flow is the **reference
 * implementation**: every mechanic (state, hooks, gestures, keyboard,
 * autoplay, focus trap, image preload, toast, sharingBusy spinner,
 * AnimatePresence, CardSlide motion, GradeCelebration, WrappedAudio) is
 * copied 1:1 from there.
 *
 * Caller responsibilities:
 *   - Build `WrappedCardSpec[]` (kinds, image URLs, share URLs, filenames,
 *     per-card celebration tier).
 *   - Provide audio mood (the WrappedAudio child decides whether to play).
 *   - Provide ARIA label.
 *   - Optional: per-card extra chrome (e.g. plan's "Build brand" pill on
 *     the contract card).
 *
 * Consumers:
 *   - `niche-v3/WrappedFinale.tsx` — 2 cards, debate-driven, audio = mood.
 *   - `plan/WrappedPNGPlayer.tsx`  — 7 cards, plan share endpoints, audio
 *     defaults to "stable" + last-card "Build brand" pill.
 */

import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { Loader2, Share2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { WrappedAudio } from "@/components/niche-v3/shared/WrappedAudio";

/* ────────────────────────────── Public types ────────────────────────────── */

export type WrappedCelebrationTier = "triple" | "single" | "sparkle" | "none";

export interface WrappedCardSpec {
  /** Stable identifier — used for React key + ARIA + image alt. */
  kind: string;
  /**
   * Full satori PNG URL (already cache-busted) rendered as full-bleed image.
   * Mutually exclusive with `node` — when `node` is provided, this is ignored
   * and no preload / loader UI is shown for this card.
   */
  imageUrl?: string;
  /**
   * Live React content rendered as the card body instead of a satori PNG.
   * Used by Brand v2 where 9 of 10 cards are interactive (CTA buttons,
   * MJ-prompt copy, framer-motion intra-card animations) and Satori PNG
   * generation is impractical. When set, takes precedence over `imageUrl`
   * — image preload, ready-loader, and onError fallback are skipped for
   * this card. Chrome (progress bar, share, close, paused, audio,
   * celebration) remains identical regardless of body type.
   */
  node?: ReactNode;
  /**
   * Full satori PNG URL used for the per-card Share button. When `undefined`
   * the Share button is hidden. Usually identical to `imageUrl`. Brand cards
   * with a `node` body may still set this to enable share-card download
   * (press-release, founder-card).
   */
  shareImageUrl?: string;
  /** Filename used for download (without `.png`). */
  shareFilenameBase: string;
  /** Sparkle ritual tier overlaid above the image. Defaults to `none`. */
  celebrationTier?: WrappedCelebrationTier;
}

export interface WrappedStoriesPlayerProps {
  open: boolean;
  onClose: () => void;
  cards: WrappedCardSpec[];
  /** ARIA label / sr-only title for the dialog. */
  srLabel: string;
  /** Mood signal forwarded to <WrappedAudio> — child decides whether to play. */
  audioMood: "hot" | "stable" | "frontier";
  /**
   * Per-card extra chrome rendered next to the Close button. E.g. plan's
   * "Build brand →" pill on the last card. Return `null` to render nothing.
   */
  extraTopRightChrome?: (
    currentCard: WrappedCardSpec,
    index: number,
    isLast: boolean,
  ) => ReactNode;
  /** "auto" fires autoplay immediately; "manual" waits for first tap. */
  trigger?: "auto" | "manual";
  /** ms per card. Default 4000. */
  cardDurationMs?: number;
  /**
   * One-shot side-effect fired the first time the user reaches the last
   * card. Brand v2 uses it to POST `wrapped-played`. Wrapper is responsible
   * for keeping the call idempotent if needed.
   */
  onFinish?: () => void;
  /**
   * Per-card hook fired after a successful share-card download. Brand v2
   * uses it to POST `exports-available` so workspace tiles reflect the
   * export. Errors swallowed by the player; wrapper handles fire-and-forget.
   */
  onAfterShare?: (card: WrappedCardSpec, index: number) => void;
  /**
   * Per-card hook fired BEFORE the share-card fetch starts. Used by analytics
   * wrappers to record click ATTEMPTS (not successes) so funnels capture
   * intent even when the PNG render times out or 404s. Errors must not throw.
   */
  onBeforeShare?: (card: WrappedCardSpec, index: number) => void;
}

/* ────────────────────────────── Config ────────────────────────────── */

const DEFAULT_CARD_DURATION_MS = 4000;
const TICK_MS = 60;

/* ────────────────────────────── Focus trap helper ────────────────────────────── */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
  );
}

/* ────────────────────────────── Share handler ────────────────────────────── */

async function downloadCard(
  shareImageUrl: string,
  filenameBase: string,
): Promise<"downloaded" | "error"> {
  try {
    const res = await fetch(shareImageUrl);
    if (!res.ok) return "error";
    const blob = await res.blob();
    const filename = `${filenameBase}.png`;
    const link = document.createElement("a");
    const href = URL.createObjectURL(blob);
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
    return "downloaded";
  } catch {
    return "error";
  }
}

/* ────────────────────────────── Sparkle celebration ────────────────────────────── */

/**
 * Per-tier visual signature for SparkleBurst.
 *
 *   triple  → 3 staggered bursts, 18 particles each, dual-tone amber, gold
 *             ring expansion + 2 secondary halo rings. Feels like a peak
 *             moment (S / A+).
 *   single  → 1 burst, 14 particles, single ring expansion, slightly bigger
 *             particle glow than `sparkle`. Solid wow without overload (A/A-).
 *   sparkle → 1 burst, 8 particles, no ring. The original baseline, just
 *             quieter so it leaves room for the higher tiers to feel earned
 *             (B+/B/B-).
 *
 * `none` short-circuits at the GradeCelebration level — SparkleBurst never
 * mounts for that tier.
 *
 * Rationale: we need the difference between tiers to read at a glance ("the
 * S-tier reveal felt different from the B-tier reveal"), but we deliberately
 * stay in the same visual vocabulary (amber sparkles, no confetti) — a B-tier
 * reveal still feels like a Foundry moment, just quieter. Apple/Linear-tier
 * polish: emotional but restrained.
 */
const TIER_CONFIG: Record<
  Exclude<WrappedCelebrationTier, "none">,
  {
    /** Number of particles per burst. */
    particleCount: number;
    /** Number of staggered bursts (each separated by `burstDelayStepMs`). */
    burstCount: number;
    /** ms between successive bursts when burstCount > 1. */
    burstDelayStepMs: number;
    /** Angle offset (radians) added per burst — keeps overlapping bursts distinct. */
    burstAngleOffset: number;
    /** Particle size class — Tailwind size-* */
    particleSizeClass: string;
    /** Particle glow intensity (Tailwind shadow class). */
    particleShadowClass: string;
    /** Use dual-tone palette (amber-300 + amber-200) for visual depth. */
    dualTone: boolean;
    /** Render the primary expanding ring. */
    showRing: boolean;
    /** Render secondary halo rings (only relevant if showRing). */
    haloCount: number;
  }
> = {
  triple: {
    particleCount: 22,
    burstCount: 3,
    burstDelayStepMs: 220,
    burstAngleOffset: Math.PI / 11,
    particleSizeClass: "size-1.5",
    particleShadowClass: "shadow-[0_0_10px_rgba(251,191,36,0.9)]",
    dualTone: true,
    showRing: true,
    haloCount: 2,
  },
  single: {
    particleCount: 14,
    burstCount: 1,
    burstDelayStepMs: 0,
    burstAngleOffset: 0,
    particleSizeClass: "size-2",
    particleShadowClass: "shadow-[0_0_10px_rgba(251,191,36,0.85)]",
    dualTone: false,
    showRing: true,
    haloCount: 0,
  },
  sparkle: {
    particleCount: 8,
    burstCount: 1,
    burstDelayStepMs: 0,
    burstAngleOffset: 0,
    particleSizeClass: "size-1.5",
    particleShadowClass: "shadow-[0_0_8px_rgba(251,191,36,0.8)]",
    dualTone: false,
    showRing: false,
    haloCount: 0,
  },
};

function SparkleBurst({
  active,
  tier,
}: {
  active: boolean;
  tier: Exclude<WrappedCelebrationTier, "none">;
}) {
  // Inline amber sparkle burst rendered as div + framer-motion (no extra lib).
  // Tier matrix wired in S99 — was identical for every non-"none" tier
  // pre-S99 (BILL-02 / BILL-03), now visually distinct per CelebrationTier
  // (BILL-04 wires the type to actual variants in WrappedFinale /
  // WrappedPNGPlayer). See TIER_CONFIG above for per-tier knobs.
  const config = TIER_CONFIG[tier];
  const burstSeeds = useMemo(() => {
    return Array.from({ length: config.burstCount }).map((_, burstIdx) => {
      return Array.from({ length: config.particleCount }).map((_, i) => {
        // Deterministic pseudo-random per-particle so SSR + CSR match.
        const seed =
          Math.sin((i + 1) * 12.9898 + (burstIdx + 1) * 78.233) * 43758.5453;
        const normalized = seed - Math.floor(seed);
        return {
          angle:
            (i / config.particleCount) * Math.PI * 2 +
            burstIdx * config.burstAngleOffset,
          distance: 80 + normalized * 40,
          // Per-particle stagger inside the burst (0–150ms) plus the burst
          // start delay so bursts cascade rather than fire simultaneously.
          delay:
            (1 - normalized) * 0.15 +
            (burstIdx * config.burstDelayStepMs) / 1000,
          // Half the particles in dual-tone bursts use the lighter amber-200
          // shade — gives the cluster a sense of layered depth.
          tone: config.dualTone && i % 2 === 0 ? "light" : "base",
        };
      });
    });
  }, [config]);

  if (!active) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
      data-celebration-tier={tier}
    >
      {/* Primary expanding ring + halos — only on `triple` and `single`.
       *  Ring scales 0→2.5x and fades out, mimicking a bell-strike pulse.
       *  `triple` adds two more halo rings staggered, giving the moment
       *  more weight. */}
      {config.showRing && (
        <motion.span
          aria-hidden
          initial={{ scale: 0, opacity: 0.7 }}
          animate={{ scale: 2.5, opacity: 0 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="absolute size-24 rounded-full border-2 border-amber-300/70 shadow-[0_0_24px_rgba(251,191,36,0.45)]"
        />
      )}
      {config.showRing &&
        Array.from({ length: config.haloCount }).map((_, haloIdx) => (
          <motion.span
            key={`halo-${haloIdx}`}
            aria-hidden
            initial={{ scale: 0, opacity: 0.5 }}
            animate={{ scale: 3.2, opacity: 0 }}
            transition={{
              duration: 1.4,
              delay: 0.18 + haloIdx * 0.18,
              ease: "easeOut",
            }}
            className="absolute size-24 rounded-full border border-amber-200/60"
          />
        ))}

      {/* Particle bursts — one or more concentric clusters per tier. */}
      {burstSeeds.map((particles, burstIdx) =>
        particles.map((p, i) => {
          const x = Math.cos(p.angle) * p.distance;
          const y = Math.sin(p.angle) * p.distance;
          const colorClass =
            p.tone === "light" ? "bg-amber-200" : "bg-amber-300";
          return (
            <motion.span
              key={`b${burstIdx}-${i}`}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
              animate={{ x, y, opacity: [0, 1, 0], scale: [0, 1, 0.6] }}
              transition={{
                duration: 1.2,
                delay: p.delay,
                ease: "easeOut",
              }}
              className={cn(
                "absolute rounded-full",
                config.particleSizeClass,
                colorClass,
                config.particleShadowClass,
              )}
            />
          );
        }),
      )}
    </div>
  );
}

/* ────────────────────────────── Main ────────────────────────────── */

export function WrappedStoriesPlayer({
  open,
  onClose,
  cards,
  srLabel,
  audioMood,
  extraTopRightChrome,
  trigger = "auto",
  cardDurationMs = DEFAULT_CARD_DURATION_MS,
  onFinish,
  onAfterShare,
  onBeforeShare,
}: WrappedStoriesPlayerProps) {
  const reduced = useReducedMotion();
  const cardCount = cards.length;
  const finishedRef = useRef(false);

  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(trigger === "manual");
  const [toast, setToast] = useState<string | null>(null);
  const [loadedImageUrls, setLoadedImageUrls] = useState<Set<string>>(
    () => new Set(),
  );
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(
    () => new Set(),
  );
  const toastTimerRef = useRef<number | null>(null);
  const preloadedImageUrlsRef = useRef<Set<string>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const titleId = useId();

  // Node-mode cards have no PNG to preload or track. Image-mode cards keep
  // an empty-string fallback so positional indexing into `cardUrls` matches
  // `cards` 1:1 (callers without an imageUrl are misconfigured but we don't
  // want a positional shift to mask the bug).
  const cardUrls = useMemo(
    () => cards.map((card) => (card.node ? "" : card.imageUrl ?? "")),
    [cards],
  );

  const markImageLoaded = useCallback((url: string) => {
    setLoadedImageUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  const markImageFailed = useCallback((url: string) => {
    setFailedImageUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  /*
   * Warm every Wrapped PNG as soon as the parent hydrates, not when the
   * overlay opens. Satori/Resvg renders are the slow part; by firing these
   * image requests on page entry, later cards are usually in browser cache
   * by the time the user advances to them.
   */
  useEffect(() => {
    let cancelled = false;
    for (const url of cardUrls) {
      if (!url) continue; // node-mode card — nothing to preload
      if (preloadedImageUrlsRef.current.has(url)) continue;
      preloadedImageUrlsRef.current.add(url);
      const img = new Image();
      img.decoding = "async";
      img.onload = () => {
        if (!cancelled) markImageLoaded(url);
      };
      img.onerror = () => {
        if (!cancelled) markImageFailed(url);
      };
      img.src = url;
    }
    return () => {
      cancelled = true;
    };
  }, [cardUrls, markImageLoaded, markImageFailed]);

  /* reset on open */
  useEffect(() => {
    if (open) {
      setIndex(0);
      setProgress(0);
      setPaused(trigger === "manual");
    }
  }, [open, trigger]);

  /* body scroll lock */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  /* focus management */
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current =
      (document.activeElement as HTMLElement | null) ?? null;
    const root = containerRef.current;
    if (!root) return;
    const raf = window.requestAnimationFrame(() => {
      const closeBtn = root.querySelector<HTMLElement>('[data-wrapped-close="true"]');
      const target = closeBtn ?? getFocusable(root)[0] ?? root;
      target.focus();
    });
    return () => {
      window.cancelAnimationFrame(raf);
      const prev = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (prev) {
        window.setTimeout(() => {
          try {
            prev.focus();
          // intentional: previously-focused element may be gone from DOM after re-render
          } catch {
            /* gone — ignore */
          }
        }, 0);
      }
    };
  }, [open]);

  const currentCard = cards[index] ?? cards[0];
  const currentIsNode = Boolean(currentCard?.node);
  const currentCardUrl = currentIsNode
    ? ""
    : (cardUrls[index] ?? currentCard?.imageUrl ?? "");
  // Node-mode cards are always considered "ready" — there's no PNG to wait
  // for. Without this short-circuit autoplay would stall forever on a brand
  // card (currentImageReady would be false because nothing was preloaded).
  const currentImageReady =
    currentIsNode ||
    loadedImageUrls.has(currentCardUrl) ||
    failedImageUrls.has(currentCardUrl);
  const currentImageFailed =
    !currentIsNode && failedImageUrls.has(currentCardUrl);
  const isLast = index === cardCount - 1;
  const autoplayDisabled =
    reduced || paused || !currentImageReady || (isLast && progress >= 1);

  /* autoplay tick */
  useEffect(() => {
    if (!open || autoplayDisabled) return;
    const id = window.setInterval(() => {
      setProgress((p) => {
        const next = p + TICK_MS / cardDurationMs;
        return next >= 1 ? 1 : next;
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [open, autoplayDisabled, index, cardDurationMs]);

  /* advance when progress hits 1 */
  useEffect(() => {
    if (!open) return;
    if (progress >= 1 && !autoplayDisabled && index < cardCount - 1) {
      setIndex((i) => i + 1);
      setProgress(0);
    }
  }, [progress, autoplayDisabled, index, open, cardCount]);

  /* one-shot finish hook — fires when user reaches the last card */
  useEffect(() => {
    if (!open) return;
    if (!isLast) return;
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinish?.();
  }, [open, isLast, onFinish]);

  /* reset finished flag when overlay re-opens */
  useEffect(() => {
    if (!open) finishedRef.current = false;
  }, [open]);

  const goTo = useCallback(
    (next: number) => {
      const bounded = Math.max(0, Math.min(cardCount - 1, next));
      setIndex(bounded);
      setProgress(0);
      setPaused(false);
    },
    [cardCount],
  );
  const goNext = useCallback(() => {
    if (index < cardCount - 1) goTo(index + 1);
  }, [index, cardCount, goTo]);
  const goPrev = useCallback(() => {
    if (index > 0) goTo(index - 1);
  }, [index, goTo]);
  const togglePause = useCallback(() => {
    setPaused((p) => !p);
  }, []);

  /* keyboard + focus trap */
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
        return;
      }
      if (e.key === " ") {
        e.preventDefault();
        togglePause();
        return;
      }
      if (e.key === "Tab") {
        const root = containerRef.current;
        if (!root) return;
        const focusables = getFocusable(root);
        if (focusables.length === 0) {
          e.preventDefault();
          root.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!active || !root.contains(active)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, goNext, goPrev, togglePause]);

  /* gesture handlers */
  const pressRef = useRef<{ x: number; y: number; t: number } | null>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    pressRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const start = pressRef.current;
    pressRef.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dt = Date.now() - start.t;

    // swipe down to close
    if (dy > 80 && Math.abs(dx) < 80 && dt < 600) {
      onClose();
      return;
    }
    // taps only if short + small movement
    if (dt > 400 || Math.abs(dx) > 20 || Math.abs(dy) > 20) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - start.x + (start.x - rect.left)) / rect.width;
    if (relX < 0.3) goPrev();
    else if (relX > 0.7) goNext();
    else togglePause();
  };

  /* toast helper */
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 1500);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  /** True while the share-card PNG is being fetched + downloaded. */
  const [sharingBusy, setSharingBusy] = useState(false);

  const handleShare = useCallback(
    async (card: WrappedCardSpec, cardIndex: number) => {
      if (!card.shareImageUrl) return;
      // Fire onBeforeShare ATTEMPT hook before the long fetch — wrapper uses
      // this for analytics. Must never throw; we wrap defensively in case a
      // future call site forgets the contract.
      try {
        onBeforeShare?.(card, cardIndex);
      } catch (err) {
        // intentional: analytics hook errors must not block the user's share
        void err;
      }
      setSharingBusy(true);
      try {
        const status = await downloadCard(
          card.shareImageUrl,
          card.shareFilenameBase,
        );
        if (status === "downloaded") {
          showToast("Saved");
          onAfterShare?.(card, cardIndex);
        } else {
          showToast("Could not generate");
        }
      } finally {
        setSharingBusy(false);
      }
    },
    [showToast, onAfterShare, onBeforeShare],
  );

  /* aria-live text */
  const liveText = `Card ${index + 1} of ${cardCount}`;

  const currentTrap = (e: ReactKeyboardEvent) => {
    void e;
  };

  if (!open) return null;
  if (!currentCard) return null;

  const showShare = !!currentCard.shareImageUrl;
  const tier: WrappedCelebrationTier = currentCard.celebrationTier ?? "none";

  return (
    <AnimatePresence>
      <motion.div
        key="wrapped-stories-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={containerRef}
        tabIndex={-1}
        onKeyDown={currentTrap}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        // Solid black (was bg-black/95 + backdrop-blur-md). The 5% transparency
        // let blurred page motion (whileInView animations on the plan page)
        // bleed through, which read visually as "the card is scrollable" — it
        // is not, body is overflow:hidden. Solid background kills the illusion.
        className="fixed inset-0 z-50 flex items-center justify-center bg-black outline-none"
      >
        <span id={titleId} className="sr-only">
          {srLabel}
        </span>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {liveText}
        </div>

        {/* Card frame */}
        <div
          className={cn(
            "relative flex h-[100svh] w-screen flex-col overflow-hidden bg-zinc-950",
            // Desktop frame caps at 812×450 BUT must never exceed the viewport.
            // Without min(...) a window <812px tall pushes the frame off-screen
            // (chrome at top + bottom get clipped, looks like the card is
            // "scrollable"). The min() clamps to whichever is smaller.
            "md:h-[min(812px,100svh)] md:w-[min(450px,100vw)] md:rounded-3xl md:shadow-2xl md:ring-1 md:ring-white/10",
          )}
        >
          {/* Progress segments — N segments */}
          <div
            role="progressbar"
            aria-valuenow={Math.round(((index + progress) / cardCount) * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Card ${index + 1} of ${cardCount} progress`}
            className="relative z-20 flex gap-1 px-4 pt-[calc(0.75rem+var(--spacing-safe-top))]"
          >
            {cards.map((_, i) => {
              const fill = i < index ? 1 : i === index ? progress : 0;
              return (
                <div
                  key={i}
                  aria-hidden
                  className="relative h-[3px] flex-1 overflow-hidden rounded-full bg-white/15"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-amber-400"
                    style={{ width: `${Math.min(100, Math.max(0, fill * 100))}%` }}
                  />
                </div>
              );
            })}
          </div>

          {/* Top-right chrome: optional caller-supplied extras + Close.
           *  Top offset adds safe-area-inset-top so the button clears the
           *  iPhone notch when the overlay is full-bleed. */}
          <div className="absolute right-3 top-[calc(1.5rem+var(--spacing-safe-top))] z-30 flex items-center gap-2">
            {extraTopRightChrome
              ? extraTopRightChrome(currentCard, index, isLast)
              : null}
            <button
              type="button"
              data-wrapped-close="true"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close Wrapped"
              className={cn(
                "inline-flex items-center gap-1.5",
                "rounded-full border border-white/10 bg-black/40 px-3 py-1.5 backdrop-blur",
                "text-[12px] font-semibold text-white/80 transition-colors hover:bg-black/60",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60",
              )}
            >
              <X className="size-3.5" aria-hidden />
              Close
            </button>
          </div>

          {/* Top-left chrome: per-card Share + Paused badge.
           *  Share lives in overlay chrome so it never covers the card art.
           *  Disabled + spinner while a download is being fetched (~5-25 s)
           *  so the user knows their tap registered and isn't tempted to
           *  spam the button. */}
          <div className="absolute left-3 top-[calc(1.5rem+var(--spacing-safe-top))] z-30 flex flex-col items-start gap-2">
            {showShare && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (sharingBusy) return;
                  void handleShare(currentCard, index);
                }}
                disabled={sharingBusy}
                aria-busy={sharingBusy}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5",
                  "font-heading text-[12px] font-bold text-zinc-950 shadow-lg shadow-black/25",
                  "transition-transform hover:scale-[1.03] active:scale-[0.98]",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950",
                  "disabled:cursor-wait disabled:opacity-90 disabled:hover:scale-100",
                )}
              >
                {sharingBusy ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <Share2 className="size-3.5" aria-hidden />
                )}
                {sharingBusy ? "Saving…" : "Share"}
              </button>
            )}
            {paused && (
              <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] uppercase tracking-widest text-white/50 backdrop-blur">
                Paused
              </span>
            )}
          </div>

          {/* Tap + content area */}
          <div
            className="relative flex-1 select-none"
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
          >
            <AnimatePresence mode="wait">
              <CardSlide key={`${currentCard.kind}-${index}`} reduced={!!reduced}>
                {currentIsNode ? (
                  /* Node-mode (Brand v2): caller-provided React subtree. No
                   *  preload, no loader, no onError fallback — wrapper is
                   *  responsible for its own loading/empty states. */
                  <div className="absolute inset-0 flex flex-col">
                    {currentCard.node}
                  </div>
                ) : (
                  <>
                    {!currentImageReady && (
                      <div className="absolute inset-0 flex items-center justify-center bg-zinc-950">
                        <div className="flex flex-col items-center gap-4 px-8 text-center">
                          <div className="h-1.5 w-28 overflow-hidden rounded-full bg-white/10">
                            <div className="h-full w-1/2 animate-pulse rounded-full bg-amber-400/80" />
                          </div>
                          <div className="font-heading text-[13px] font-semibold uppercase tracking-[0.18em] text-white/55">
                            Preparing card
                          </div>
                        </div>
                      </div>
                    )}
                    {currentImageFailed && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-950 px-8 text-center">
                        <div className="font-heading text-[13px] font-semibold uppercase tracking-[0.18em] text-white/55">
                          Still rendering
                        </div>
                      </div>
                    )}
                    {/* Full-bleed satori PNG — single source of truth: same visual
                     *  the user actually shares. Interactive chrome (progress bar,
                     *  close, share button, extra chrome) draws on top. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={currentCardUrl}
                      alt={`Wrapped card ${index + 1}: ${currentCard.kind}`}
                      className={cn(
                        "absolute inset-0 h-full w-full object-cover",
                        currentImageFailed && "opacity-0",
                      )}
                      decoding="async"
                      draggable={false}
                      loading="eager"
                      onLoad={() => markImageLoaded(currentCardUrl)}
                      onError={() => markImageFailed(currentCardUrl)}
                    />
                  </>
                )}
                {/* Sparkle ritual overlay — driven by per-card celebrationTier.
                 *  Works for both image and node modes. */}
                {tier !== "none" && (
                  <GradeCelebration
                    tier={tier}
                    reducedMotion={!!reduced}
                    active={true}
                  />
                )}
              </CardSlide>
            </AnimatePresence>
          </div>

          {/* Toast */}
          <AnimatePresence>
            {toast && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.18 }}
                className="pointer-events-none absolute inset-x-0 bottom-28 z-30 flex justify-center"
              >
                <div className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-[13px] font-semibold text-zinc-900 shadow-lg">
                  {toast}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Gesture-gated ambient audio loop */}
        <WrappedAudio mood={audioMood} active={open} />
      </motion.div>
    </AnimatePresence>
  );
}

/* ────────────────────────────── Slide wrapper (shared motion) ────────────────────────────── */

function CardSlide({
  children,
  reduced,
}: {
  children: ReactNode;
  reduced: boolean;
}) {
  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.02 }}
      transition={{ duration: reduced ? 0.15 : 0.3, ease: "easeOut" }}
      className="absolute inset-0 flex flex-col"
    >
      {children}
    </motion.div>
  );
}

/* ──────────────────────────── Grade celebration layer ─────────────────────── */

/**
 * Overlays a tier-distinct sparkle burst on top of any card whose spec carries
 * a non-"none" celebrationTier.
 *
 * S83 — canvas-confetti dropped sitewide. Previously top-tier grades fired
 * a tier-aware multi-burst ritual via canvas-confetti; mid-tier (B) used
 * the inline <SparkleBurst /> as the celebration; bottom (C) was silent.
 * The dependency was a 14 KB GZIP overhead for a one-shot effect, and the
 * brand is shifting away from generic celebration UX (the niche reveal IS
 * the moment). SparkleBurst stays — it's pure CSS keyframes + framer-motion,
 * no library, fits the foil aesthetic.
 *
 * S99 (BILL-02..04) — re-introduced tier-distinctness. SparkleBurst now
 * consumes the `tier` value via TIER_CONFIG and produces visually distinct
 * variants:
 *   triple  (S/A+) → 3 staggered bursts + ring + 2 halos + dual-tone
 *   single  (A/A-) → 1 burst + ring (no halos) + bigger particles
 *   sparkle (B+/B/B-) → 1 quiet burst, no ring (baseline)
 *   none    (C and lower) → render nothing
 *
 * Reduced-motion preference still wins: when `reducedMotion`, the layer
 * renders nothing regardless of tier (no animation = no celebration UI).
 */
function GradeCelebration({
  tier,
  active,
  reducedMotion,
}: {
  tier: WrappedCelebrationTier;
  active: boolean;
  reducedMotion: boolean;
}) {
  // `active` is part of the public contract (component remounts on card
  // swap) and remains for future per-tier behavior; intentionally unused
  // here so SparkleBurst always animates on first mount of the celebrated
  // card.
  void active;
  if (reducedMotion) return null;
  if (tier === "none") return null;
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <SparkleBurst active tier={tier} />
    </div>
  );
}

